import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import rg4js from "raygun4js";
import React, { useState } from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import {
  type Case,
  DocumentStatus,
  DocumentSubType,
  IncludedDocuments,
} from "@/briefpoint-client";
import { toast } from "@/components/ui/toast";
import { useDocumentApi } from "@/hooks/useApi";
import useDocuments from "@/hooks/useDocuments";
import type {
  ClientResponse,
  DocumentClient,
  DocumentResponse,
  JurisdictionResponse,
} from "@/lib/types";
import {
  addUploadDate,
  getDocumentSubTypeShortName,
  normalizeError,
} from "@/lib/utils";
import { DropDownFilterList, type DropDownItem } from "../DropDownFilterList";
import DocumentsTable from "./DocumentsTable";

interface CaseDiscoveryProps {
  id: string;
  jurisdiction: JurisdictionResponse | undefined;
  _case: Case | undefined;
  clients: ClientResponse[] | undefined;
  chooseFile: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  nonArchivedDocs: DocumentClient[] | undefined;
  isUploading?: boolean;
}

export default function CaseDiscovery(props: CaseDiscoveryProps) {
  const documentApi = useDocumentApi();
  const queryClient = useQueryClient();
  const [partyFilter, setPartyFilter] = useState<string[]>([]);
  const [docFilter, setDocFilter] = useState<string[]>([]);
  const [clientFilter, setClientFilter] = useState<string[]>([]);
  const [nameFilter, setNameFilter] = useState<string>("");
  const documentGetQueryKey = ["documentApi.documentGetList", props.id];
  const [, , , , , , downloadForm, , downloadRequest] = useDocuments(
    true,
    props.id,
  );

  function handleClearEvent() {
    setPartyFilter([]);
    setDocFilter([]);
    setClientFilter([]);
  }

  // QUERIES
  // Retrieve documents
  const documentsQuery = useQuery({
    queryKey: documentGetQueryKey,
    queryFn: async () => {
      const documents = !props.id
        ? ((await documentApi.documentGetList({
            includedDocuments: IncludedDocuments.NonArchived,
          })) as DocumentResponse[])
        : ((await documentApi.documentGetCaseDocuments({
            caseId: props.id,
            includedDocuments: IncludedDocuments.NonArchived,
          })) as DocumentResponse[]);
      return documents;
    },
    refetchInterval: (query) => {
      const documents = query.state.data as DocumentResponse[] | undefined;
      if (!documents) return 1500; // Poll while loading to get initial data

      // Check if any document has a status that indicates it's still processing
      const processingStatuses = [
        DocumentStatus.Unknown,
        DocumentStatus.Uploaded,
        DocumentStatus.Processing,
        DocumentStatus.Deleted,
        DocumentStatus.Error,
      ];

      const hasProcessingDocuments = documents.some((doc) =>
        processingStatuses.includes(doc.status),
      );

      return hasProcessingDocuments ? 1500 : false; // Poll every 1.5 seconds if there are processing documents
    },
    select: (data: DocumentResponse[]) => {
      return {
        documents: data.map((d) => addUploadDate(d)),
        nonArchivedDocs: data
          .map((d) => addUploadDate(d))
          .filter((d) => !d.isArchived),
      };
    },
  });

  // MUTATIONS
  // Update document
  const updateDocumentMutation = useMutation({
    mutationFn: async (doc: DocumentClient) => {
      await documentApi.documentUpdateDocument({
        documentId: doc.id,
        document: doc,
      });
      return doc;
    },
    onMutate: async (doc: DocumentClient) => {
      // Cancel any outgoing refetch so that we don't overwrite any other changes
      await queryClient.cancelQueries({
        queryKey: documentGetQueryKey,
      });

      // Snapshot the previous value of the query data
      const previousData = queryClient.getQueryData(documentGetQueryKey);

      // Optimistically update the query data
      queryClient.setQueryData(
        documentGetQueryKey,
        (oldData: DocumentResponse[]) => {
          if (!oldData) return oldData;
          return oldData.map((d) => (d.id === doc.id ? { ...d, ...doc } : d));
        },
      );

      return { previousData };
    },
    onSuccess: (doc: DocumentClient) => {
      toast.success(`${doc.name} was updated successfully.`);
    },
    onError: (error, doc: DocumentClient, context) => {
      // Restore previous data on error
      if (context?.previousData) {
        queryClient.setQueryData(documentGetQueryKey, context.previousData);
      }
      toast.error(
        `There was an error updating ${doc.name}. Please try again later.`,
      );
      rg4js("send", {
        error: normalizeError(error),
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: documentGetQueryKey,
      });
    },
  });

  //Archive files
  const archiveFilesMutation = useMutation({
    mutationFn: async (doc: DocumentClient) => {
      doc.isArchived = true;
      await documentApi.documentUpdateDocument({
        documentId: doc.id,
        document: doc,
      });
      return doc;
    },
    onMutate: async (doc: DocumentClient) => {
      // Cancel any outgoing refetch so that we don't overwrite any other changes
      await queryClient.cancelQueries({
        queryKey: documentGetQueryKey,
      });

      // Snapshot the previous value of the query data
      const previousData = queryClient.getQueryData(documentGetQueryKey);

      // Optimistically update the query data
      queryClient.setQueryData(
        documentGetQueryKey,
        (oldData: DocumentResponse[]) => {
          if (!oldData) return oldData;
          return oldData.map((d) =>
            d.id === doc.id ? { ...d, isArchived: true } : d,
          );
        },
      );

      return { previousData };
    },
    onSuccess: (doc: DocumentClient) => {
      toast.success(`${doc.name} was archived successfully.`);
    },
    onError: (error, doc: DocumentClient, context) => {
      // Restore previous data on error
      if (context?.previousData) {
        queryClient.setQueryData(documentGetQueryKey, context.previousData);
      }
      toast.error(
        `There was an error archiving ${doc.name}. Please try again later.`,
      );
      rg4js("send", {
        error: normalizeError(error),
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: documentGetQueryKey,
      });
    },
  });

  function getClientName(
    id: string | undefined | null,
    clients: ClientResponse[] | undefined,
  ) {
    if (!clients || !id) return "";
    return clients.find((c) => c.id === id)?.name ?? "";
  }

  function getFilteredDocs(
    documents: DocumentClient[] | undefined,
    nameFilter: string | undefined,
    partyFilter: string[] | undefined,
    docFilter: string[] | undefined,
    clientFilter: string[] | undefined,
  ) {
    if (!documents) {
      return documents;
    }
    let filtered = documents;
    if (nameFilter && nameFilter !== "") {
      filtered = filtered.filter(
        (d) =>
          d.name
            ?.toLocaleLowerCase()
            .includes(nameFilter.toLocaleLowerCase()) ||
          d.info?.propoundingPartyName?.includes(nameFilter) ||
          d.info?.respondingPartyName?.includes(nameFilter),
      );
    }
    if (partyFilter?.length) {
      filtered = filtered.filter(
        (d) =>
          partyFilter.includes(d.info?.propoundingPartyName ?? "") ||
          partyFilter.includes(d.info?.respondingPartyName ?? ""),
      );
    }
    if (docFilter?.length) {
      filtered = filtered.filter((d) =>
        docFilter.includes(
          getDocumentSubTypeShortName(
            d.info?.documentSubType ?? DocumentSubType.Unknown,
            props._case?.jurisdiction ?? "",
          ),
        ),
      );
    }
    if (clientFilter?.length) {
      filtered = filtered.filter((d) =>
        clientFilter.includes(getClientName(d.clientId, props.clients)),
      );
    }
    return filtered;
  }

  function getFilters() {
    function getDisplay(active: string[], df: string) {
      if (!active.length) {
        return df;
      }
      let ret = `${active[0]}`;

      if (active.length > 1) {
        ret += ` (+${active.length - 1})`;
      }
      return ret;
    }

    function getUniqueItems(
      documents: DocumentClient[] | undefined | null,
      getItemName: (doc: DocumentClient) => string,
    ): DropDownItem[] {
      return (
        documents
          ?.flatMap((d) => ({ name: getItemName(d), id: getItemName(d) }))
          .filter((p) => p.name !== "")
          .reduce<DropDownItem[]>((acc, current) => {
            if (!acc.some((item) => item.name === current.name)) {
              acc.push(current);
            }
            return acc;
          }, []) ?? []
      );
    }

    let filteredDocs = getFilteredDocs(
      documentsQuery.data?.documents ?? [],
      nameFilter,
      undefined,
      docFilter,
      clientFilter,
    );
    const respondingPartyNames = getUniqueItems(
      filteredDocs,
      (d) => d.info?.respondingPartyName ?? "",
    );
    const propoundingPartyNames = getUniqueItems(
      filteredDocs,
      (d) => d.info?.propoundingPartyName ?? "",
    );

    let allParties = [...respondingPartyNames, ...propoundingPartyNames];
    allParties =
      allParties.reduce<DropDownItem[]>((acc, current) => {
        if (!acc.some((item) => item.name === current.name)) {
          acc.push(current);
        }
        return acc;
      }, []) ?? [];

    filteredDocs = getFilteredDocs(
      documentsQuery.data?.documents ?? [],
      nameFilter,
      partyFilter,
      undefined,
      clientFilter,
    );
    const docTypes = getUniqueItems(filteredDocs, (d) =>
      getDocumentSubTypeShortName(
        d.info?.documentSubType ?? DocumentSubType.Unknown,
        props._case?.jurisdiction ?? "",
      ),
    );

    filteredDocs = getFilteredDocs(
      documentsQuery.data?.documents ?? [],
      nameFilter,
      partyFilter,
      docFilter,
      undefined,
    );
    const clientNames = getUniqueItems(filteredDocs, (d) =>
      getClientName(d.clientId, props.clients),
    );

    return (
      <React.Fragment>
        <span style={{ paddingRight: "4px" }}>Filter By</span>
        {!!partyFilter.length && (
          <label htmlFor="party-filter" className="filter-label">
            Party
          </label>
        )}
        <DropDownFilterList
          key="party-filter"
          text={getDisplay(partyFilter, "Party")}
          itemsList={allParties}
          activeFilter={partyFilter}
          updateFilter={setPartyFilter}
        />
        {!!docFilter.length && (
          <label htmlFor="doc-type-filter" className="filter-label">
            Doc Type
          </label>
        )}
        <DropDownFilterList
          key="documentType-filter"
          text={getDisplay(docFilter, "Doc Type")}
          itemsList={docTypes}
          activeFilter={docFilter}
          updateFilter={setDocFilter}
        />
        {!!clientFilter.length && (
          <label htmlFor="client-filter" className="filter-label">
            Client
          </label>
        )}
        <DropDownFilterList
          id="client-filter"
          key="client-filter"
          text={getDisplay(clientFilter, "Client")}
          itemsList={clientNames}
          activeFilter={clientFilter}
          updateFilter={setClientFilter}
        />
        {!!(partyFilter.length || docFilter.length || clientFilter.length) && (
          <Button
            className="clear-btn"
            variant="link"
            onClick={handleClearEvent}
          >
            Clear All
          </Button>
        )}
      </React.Fragment>
    );
  }
  return (
    <>
      <Row style={{ marginTop: "16px" }}>
        <Col key="search-actions" className="col-12 col-md-3">
          <div className="input-group input-group-search mb-1">
            <div className="input-group-prepend">
              <div className="input-group-text">⌕</div>
            </div>
            <Form.Control
              className="form-control defense-search-input"
              placeholder="Search name"
              onChange={(e) => {
                setNameFilter(e.target.value);
              }}
            />
          </div>
        </Col>
        <Col key="filters-actions" className="filters col-auto col-lg-9">
          {getFilters()}
        </Col>
      </Row>
      <Row style={{ marginTop: "8px" }}>
        <DocumentsTable
          keyPrefix="old"
          _case={props._case}
          jurisdiction={props.jurisdiction}
          documents={getFilteredDocs(
            documentsQuery.data?.nonArchivedDocs ?? [],
            nameFilter,
            partyFilter,
            docFilter,
            clientFilter,
          )}
          uploadDocAction={props.chooseFile}
          updateDocument={(doc) =>
            updateDocumentMutation.mutateAsync(doc).then(() => {})
          }
          archiveDocument={(doc) =>
            archiveFilesMutation.mutateAsync(doc).then(() => {})
          }
          downloadForm={downloadForm}
          isUploading={props.isUploading}
          downloadRequest={downloadRequest}
          clients={props.clients}
        />
      </Row>
    </>
  );
}
